import { encodingToHex, PRNG, createEmptyEncoding } from "bitframes";
import { decimalToHex, punchesOf } from "./ebcdic";
import globalPrng from "../util/global-prng.js";

export const BLEED = 1;

export const settings = {
  dimensions: [187 + BLEED * 2, 83 + BLEED * 2],
  // dimensions: [187 + BLEED * 2, 83 + BLEED * 2],
  units: "mm",
  pixelsPerInch: 300,
};

const punchStringToPunchRowBits = (str, rows = 12) => {
  const punches = str.split("-").map((n) => {
    const k = parseInt(n, 10);
    if (k === 12) return 0;
    else if (k === 11) return 1;
    // 10 and 0 are the same; this is the 'zeroth' row
    else if (k === 10 || k === 0) return 2;
    else {
      return k + 2;
    }
  });
  return Array(rows)
    .fill(0)
    .map((_, i) => {
      return punches.includes(i) ? true : false;
    });
};

function toVerts(x, y, w, h) {
  return [
    [x, y],
    [x + w, y],
    [x + w, y + h],
    [x, y + h],
  ];
}

const fillTextRotated = (
  context,
  text,
  x,
  y,
  clockwise = false,
  align = clockwise ? "right" : "left",
  baseline = "middle"
) => {
  context.textAlign = align;
  context.textBaseline = baseline;
  // const tx = i * columnWidth + x + cutWidth + innerXPad;
  // const ty = tableYStart + (1 + zonePad) * rowHeight;
  context.save();
  context.translate(x, y);
  const dir = clockwise ? 1 : -1;
  context.rotate((dir * 90 * Math.PI) / 180);
  context.fillText(text, 0, 0);
  context.restore();
};

export const sketch = (props) => {
  const { width, height, data = {} } = props;
  const {
    characterFontUrl,
    monoFontUrl,
    cacheBackground = false,
    shifting = false,
    highlightColumn = null,
    background = "#fff",
  } = data;
  let encoding = data.encoding ?? createEmptyEncoding();

  const columns = 32;
  const rows = 12;

  const text = "I";
  // const encoding = new Uint8Array(columns);
  // encoding.fill(character_to_EBCDIC(decimalToHex(" ".charCodeAt(0))));

  // text.split("").map((chr) => {
  //   const dec = chr.charCodeAt(0);
  //   const hex = decimalToHex(dec);
  //   const res = character_to_EBCDIC(hex);
  //   return hexToDecimal(res);
  // })
  // const encoding = hexToBytes("00010203");
  // const encoding = createRandomEncoding();
  // encoding[0] = ;

  // const encoding = randomBytes(32);
  // const background = "#fff";
  const decalColor = "rgba(0,0,0,1)";
  const decalLineColor = "rgba(0,0,0,0.25)";
  const smallFontColor = "rgba(0,0,0,1)";
  // const cardColor = "#eeeadf";
  // const cardColor = "#56748F";
  const cardColor = "#CCC8C3";
  const characterColor = "hsla(0,0%,0%,0.5)";
  const tabularColor = "black";
  const punchFillColor = background;
  const filledPunches = true;

  const gauss = 0.001;

  const padding = BLEED;
  const x = padding;
  const y = padding;
  const cardWidth = width - padding * 2;
  const cardHeight = height - padding * 2;

  const cutWidth = (5.84 / 187) * width;
  const cutHeight = (11.01 / 83) * height;

  const characterFontSize = Math.min(cardWidth, cardHeight) * 0.04;

  const innerXPad = cardWidth * 0.002;
  const innerYPad = innerXPad;

  const innerCardWidth = cardWidth - cutWidth * 2 - innerXPad * 2;
  const columnWidth = innerCardWidth / columns;
  const characterRowHeight = characterFontSize;
  const characterPad = characterRowHeight / 2;

  const tableYStart = y + innerYPad + characterRowHeight + characterPad;
  const tableYEnd = y + cardHeight - innerYPad - characterRowHeight;
  const tableHeight = tableYEnd - tableYStart;
  // there will be N rows that are padded after the 'zone' rows
  const zonePad = 1;
  const rowHeight = tableHeight / (rows + zonePad);
  const rowGap = rowHeight * 0.33;

  const monoFontSize = rowHeight - rowGap;
  const monoFontYOff = -monoFontSize * 0.0;
  const monoFontSmallSize = monoFontSize * 0.45;
  const monoFontSmallSizeYOff = monoFontSize * 0.1;

  const punchWidth = monoFontSize * 0.6;
  const punchHeight = monoFontSize * 1.0;
  const punchYOff = monoFontSize * 0.15;
  let cachedBackgroundCanvas, cachedBackgroundContext;

  const characterFont = loadFont({
    url: characterFontUrl,
    fontFamily: "HersheyNoaillesFutura",

    // url: "src/assets/fonts/BerkeleyMono-Regular.woff2",
    // fontFamily: "BerkeleyMono",
    fontSize: characterFontSize,
  });

  const monoFont = loadFont({
    url: monoFontUrl,
    fontFamily: "BerkeleyMono",
    fontSize: monoFontSize,
  });

  const monoFontSmall = loadFont({
    url: monoFontUrl,
    fontFamily: "BerkeleyMono",
    fontSize: monoFontSmallSize,
  });

  const load = async () => {
    await Promise.all([
      characterFont.load(),
      monoFont.load(),
      monoFontSmall.load(),
    ]);
  };

  const drawPunches = (columnData, path) => {
    for (let { punches } of columnData) {
      for (let punch of punches) {
        let [kx, ky] = punch;
        kx += (columnWidth - punchWidth) / 2;
        ky += punchYOff;
        const [ox, oy] = globalPrng.insideCircle(
          globalPrng.gaussian(0, height * gauss)
        );
        kx += ox;
        ky += oy;
        // const verts = toVerts(kx, ky, punchWidth, punchHeight);
        const verts = drawPunchHole(
          kx,
          ky,
          punchWidth,
          punchHeight,
          Math.min(punchWidth, punchHeight) * 0.075
        );
        verts.reverse();
        verts.forEach((v, i) => {
          if (i === 0) path.moveTo(...v);
          else path.lineTo(...v);
        });
        path.closePath();
      }
    }
  };

  const createColumnData = () => {
    const columnData = [];
    for (let ix = 0; ix < columns; ix++) {
      const dec = encoding[ix] || 0x00;
      const hex = decimalToHex(dec);
      const character = hex; //EBCDIC_to_character(hex, { printable: true });

      const punches = punchesOf(dec);

      const bits = punchStringToPunchRowBits(punches);

      const px = ix * columnWidth + x + cutWidth + innerXPad;
      const py = y + innerYPad;

      const data = {
        character: null,
        punches: [],
        columnIndices: [],
        rowIndices: [],
      };
      columnData.push(data);
      if (character != null) {
        const [ox, oy] = globalPrng.insideCircle(
          globalPrng.gaussian(0, height * gauss)
        );
        data.character = {
          text: character,
          x: px + ox,
          y: py + oy,
        };
      }

      for (let iy = 0; iy < rows; iy++) {
        const bit = bits[iy];

        const rowDrawIndex = iy < 2 ? iy : iy + zonePad;

        if (iy >= 2) {
          const idx = iy - 2;
          data.rowIndices.push({
            text: String(idx),
            x: px,
            y: tableYStart + rowDrawIndex * rowHeight,
          });
        }

        if (iy === 2 || iy === rows - 1) {
          data.columnIndices.push({
            text: String(ix + 1),
            x: px,
            y: tableYStart + rowDrawIndex * rowHeight,
          });
        }

        if (bit) {
          let kx = px;
          let ky = tableYStart + rowDrawIndex * rowHeight;
          data.punches.push([kx, ky]);
        }
      }
    }
    return columnData;
  };

  let columnData = createColumnData();

  const setEncoding = (enc) => {
    encoding = enc;
    columnData = createColumnData();
  };

  const render = (props) => {
    const { context, width, height } = props;

    if (cacheBackground && !cachedBackgroundCanvas) {
      cachedBackgroundCanvas = document.createElement("canvas");
      cachedBackgroundCanvas.width = width;
      cachedBackgroundCanvas.height = height;
      cachedBackgroundContext = cachedBackgroundCanvas.getContext("2d");

      renderCard(columnData, {
        context: cachedBackgroundContext,
        width,
        height,
        showEncoding: false,
        holes: false,
      });
    }

    // console.log("HI", context);
    // context.fillStyle = "blue";
    // context.fillRect(0, 0, 100, 100);

    context.save();
    let offset = [0, 0];
    // if (shifting) {
    //   const dim = Math.min(width, height);
    //   offset = globalPrng.insideCircle(globalPrng.gaussian(0, dim * 0.0));
    // }
    if (cacheBackground) {
      context.clearRect(0, 0, width, height);
      context.translate(...offset);
      context.drawImage(cachedBackgroundCanvas, 0, 0);
      fillPunches(context, columnData);
      fillEncodingText(context, columnData);
    } else {
      context.translate(...offset);
      renderCard(columnData, { ...props, showEncoding: true, holes: true });
    }
    context.restore();
  };

  const fillPunches = (context, columnData) => {
    const path = new Path2D();
    drawPunches(columnData, path);
    context.fillStyle = punchFillColor || background;
    context.fill(path);
  };

  const fillEncodingText = (context, columnData) => {
    context.strokeStyle = "hsla(0,0%,0%,0.05)";
    context.lineWidth = 0.001 * width;

    for (let { character } of columnData) {
      if (character) {
        context.font = characterFont.font;
        context.fillStyle = characterColor;
        context.textAlign = "center";
        context.textBaseline = "alphabetic";

        // context.strokeRect(
        //   character.x,
        //   character.y,
        //   columnWidth,
        //   characterRowHeight
        // );
        context.fillText(
          character.text,
          character.x + columnWidth / 2,
          character.y + characterRowHeight
        );
      }
    }
  };

  const renderCard = (
    columnData,
    { context, width, height, showEncoding = true, holes = true }
  ) => {
    context.fillStyle = background;
    context.fillRect(0, 0, width, height);

    // const prng = PRNG(encodingToHex(encoding));

    const { svg } = createCard({
      x: padding,
      y: padding,
      width: cardWidth,
      height: cardHeight,
    });

    const path = new Path2D(svg);
    if (!filledPunches && holes) {
      drawPunches(columnData, path);
    }
    context.save();
    context.clip(path);
    context.fillStyle = cardColor;
    context.fill(path);

    const decals = true;
    if (decals) {
      context.strokeStyle = decalLineColor;
      context.fillStyle = decalColor;
      context.lineWidth = height * 0.002;
      context.beginPath();

      const includeTopLine = true;
      const horizLineY0 =
        y + innerXPad + characterRowHeight + characterFontSize * 0.25;
      if (includeTopLine) {
        context.moveTo(x, horizLineY0);
        context.lineTo(x + cardWidth, horizLineY0);
      }

      const includeEdges = true;
      if (includeEdges) {
        context.moveTo(x + cutWidth + innerXPad, y);
        context.lineTo(x + cutWidth + innerXPad, y + cardHeight);
      }
      for (let layer = 0; layer < (includeEdges ? 6 : 5); layer++) {
        const vertLineX =
          (layer * 6 + 2) * columnWidth + x + cutWidth + innerXPad;
        context.moveTo(vertLineX, y);
        context.lineTo(vertLineX, y + cardHeight);
      }

      const drawTableStartLine = false;
      const zeroTableStartY = tableYStart + (2 + zonePad) * rowHeight;
      if (drawTableStartLine) {
        context.moveTo(x, zeroTableStartY);
        context.lineTo(x + cardWidth, zeroTableStartY);
      }

      context.stroke();

      if (zonePad > 0) {
        const header = ["VER", "RND"];
        const body = ["FLG", "PAL", "DIM", "PAT", "RUL", "SCL"];
        for (let i = 0; i < columns; i++) {
          const tag = i < 2 ? header[i] : body[(i - 2) % body.length];

          context.font = monoFontSmall.font;
          const tx = i * columnWidth + x + cutWidth + innerXPad;
          const ty = tableYStart + (1 + zonePad) * rowHeight;
          fillTextRotated(
            context,
            tag,
            tx + columnWidth / 2,
            ty + rowHeight - monoFontSmallSize * 0.25,
            false
          );
        }
      }

      context.font = monoFontSmall.font;
      const versionStr = "VERSION_00";
      // const textWidth = context.measureText(versionStr).width;
      const rectH = rowHeight;
      const closeTextPad = innerXPad + monoFontSmallSize / 4;
      fillTextRotated(
        context,
        versionStr,
        x + closeTextPad,
        tableYEnd - rectH - rowHeight * 2,
        false,
        undefined,
        "top"
      );
      const rectW = monoFontSmallSize / 4;
      const rectY = tableYEnd - rectH - rowHeight * 1;
      const comment = "FOR MACHINE USE";

      // zeroTableStartY + (tableYEnd - zeroTableStartY) / 2,

      fillTextRotated(
        context,
        comment,
        x + cardWidth - closeTextPad,
        tableYEnd - rectH - rowHeight * 2,
        false,
        "left",
        "ideographic"
      );
      context.fillStyle = decalColor;
      context.fillRect(x, rectY, rectW, rectH);
      context.fillRect(x + cardWidth - rectW, rectY, rectW, rectH);
    }

    context.strokeStyle = "hsla(0,0%,0%,0.05)";
    context.lineWidth = 0.001 * width;

    // draw text
    if (showEncoding) {
      fillEncodingText(context, columnData);
    }

    for (let { rowIndices, columnIndices } of columnData) {
      for (let rowIndex of rowIndices) {
        context.font = monoFont.font;
        context.fillStyle = tabularColor;
        context.textAlign = "center";
        context.textBaseline = "middle";
        context.fillText(
          rowIndex.text,
          rowIndex.x + columnWidth / 2,
          rowIndex.y + rowHeight / 2
        );
      }
      for (let columnIndex of columnIndices) {
        context.font = monoFontSmall.font;
        context.fillStyle = smallFontColor;
        context.textAlign = "center";
        context.textBaseline = "alphabetic";
        context.fillText(
          columnIndex.text,
          columnIndex.x + columnWidth / 2,
          columnIndex.y + rowHeight + monoFontSmallSize * 0.1
        );
      }
    }

    if (filledPunches && holes) {
      fillPunches(context, columnData);
    }

    if (highlightColumn != null && highlightColumn >= 0) {
      context.strokeStyle = "red";
      context.lineWidth = height * 0.005;

      const px = x + innerXPad + cutWidth + highlightColumn * columnWidth;
      const py = tableYStart;
      const pw = columnWidth;
      const ph = tableHeight;

      context.strokeStyle = "red";
      context.strokeRect(px, py, pw, ph);
    }

    context.restore();
  };

  return {
    load,
    render,
    width,
    height,
    setEncoding,
  };
};

function loadFont({ url, fontFamily, fontSize } = {}) {
  const fontStr = `${fontSize}px ${fontFamily}`;
  return {
    font: fontStr,
    async load() {
      try {
        // We ensure the font is loaded before rendering,
        // otherwise the first frame might not draw the correct font.
        const font = new window.FontFace(fontFamily, `url(${url})`);
        await font.load();
        document.fonts.add(font);
        await document.fonts.load(fontStr);
      } catch (err) {
        console.warn(err);
      }
    },
  };
}

function createCard(opts = {}) {
  const {
    x = 0,
    y = 0,
    width = 187,
    height = 83,
    cornerRadius = width * 0.02,
    cornerSmoothing = 0.6,
    preserveSmoothing = true,
    cutWidth = (5.84 / 187) * width,
    cutHeight = (11.01 / 83) * height,
  } = opts;

  const roundingAndSmoothingBudget = Math.min(width, height) / 2;
  const targetCornerRadius = Math.min(cornerRadius, roundingAndSmoothingBudget);

  const params = getPathParamsForCorner({
    cornerRadius: targetCornerRadius,
    cornerSmoothing,
    preserveSmoothing,
    roundingAndSmoothingBudget,
  });

  const svg = `
    M ${x + width - params.p} ${y}
    ${drawTopRightPath(params)}
    L ${x + width} ${y + height - params.p}
    ${drawBottomRightPath(params)}
    L ${x + params.p} ${y + height}
    ${drawBottomLeftPath(params)}
    L ${x} ${y + cutHeight}
    L ${x + cutWidth} ${y}
    Z
  `
    .replace(/[\t\s\n]+/g, " ")
    .trim();
  return { svg, cutWidth, cutHeight, cornerRadius, x, y, width, height };
}

function toRadians(degrees) {
  return (degrees * Math.PI) / 180;
}

function drawTopRightPath({ cornerRadius, a, b, c, d, p, arcSectionLength }) {
  if (cornerRadius) {
    return rounded`
    c ${a} 0 ${a + b} 0 ${a + b + c} ${d}
    a ${cornerRadius} ${cornerRadius} 0 0 1 ${arcSectionLength} ${arcSectionLength}
    c ${d} ${c}
        ${d} ${b + c}
        ${d} ${a + b + c}`;
  } else {
    return rounded`l ${p} 0`;
  }
}

function drawBottomRightPath({
  cornerRadius,
  a,
  b,
  c,
  d,
  p,
  arcSectionLength,
}) {
  if (cornerRadius) {
    return rounded`
    c 0 ${a}
      0 ${a + b}
      ${-d} ${a + b + c}
    a ${cornerRadius} ${cornerRadius} 0 0 1 -${arcSectionLength} ${arcSectionLength}
    c ${-c} ${d}
      ${-(b + c)} ${d}
      ${-(a + b + c)} ${d}`;
  } else {
    return rounded`l 0 ${p}`;
  }
}

function drawBottomLeftPath({ cornerRadius, a, b, c, d, p, arcSectionLength }) {
  if (cornerRadius) {
    return rounded`
    c ${-a} 0
      ${-(a + b)} 0
      ${-(a + b + c)} ${-d}
    a ${cornerRadius} ${cornerRadius} 0 0 1 -${arcSectionLength} -${arcSectionLength}
    c ${-d} ${-c}
      ${-d} ${-(b + c)}
      ${-d} ${-(a + b + c)}`;
  } else {
    return rounded`l ${-p} 0`;
  }
}

function rounded(strings, ...values) {
  return strings.reduce((acc, str, i) => {
    let value = values[i];

    if (typeof value === "number") {
      return acc + str + value.toFixed(4);
    } else {
      return acc + str + (value ?? "");
    }
  }, "");
}

function drawPunchHole(x, y, w, h, r = Math.min(w, h) / 8) {
  const verts = [];
  const cut = r;
  const innerW = w - cut * 2;
  const innerH = h - cut * 2;
  verts.push([x + cut, y]);
  verts.push([x + cut + innerW, y]);
  verts.push([x + w, y + cut]);
  verts.push([x + w, y + cut + innerH]);
  verts.push([x + cut + innerW, y + h]);
  verts.push([x + cut, y + h]);
  verts.push([x, y + cut + innerH]);
  verts.push([x, y + cut]);
  return verts;
}

// Source below from:
// https://github.com/phamfoo/figma-squircle/tree/main
//
// The article from figma's blog
// https://www.figma.com/blog/desperately-seeking-squircles/
//
// The original code by MartinRGB
// https://github.com/MartinRGB/Figma_Squircles_Approximation/blob/bf29714aab58c54329f3ca130ffa16d39a2ff08c/js/rounded-corners.js#L64
function getPathParamsForCorner({
  cornerRadius,
  cornerSmoothing,
  preserveSmoothing,
  roundingAndSmoothingBudget,
} = {}) {
  // From figure 12.2 in the article
  // p = (1 + cornerSmoothing) * q
  // in this case q = R because theta = 90deg
  let p = (1 + cornerSmoothing) * cornerRadius;

  // When there's not enough space left (p > roundingAndSmoothingBudget), there are 2 options:
  //
  // 1. What figma's currently doing: limit the smoothing value to make sure p <= roundingAndSmoothingBudget
  // But what this means is that at some point when cornerRadius is large enough,
  // increasing the smoothing value wouldn't do anything
  //
  // 2. Keep the original smoothing value and use it to calculate the bezier curve normally,
  // then adjust the control points to achieve similar curvature profile
  //
  // preserveSmoothing is a new option I added
  //
  // If preserveSmoothing is on then we'll just keep using the original smoothing value
  // and adjust the bezier curve later
  if (!preserveSmoothing) {
    const maxCornerSmoothing = roundingAndSmoothingBudget / cornerRadius - 1;
    cornerSmoothing = Math.min(cornerSmoothing, maxCornerSmoothing);
    p = Math.min(p, roundingAndSmoothingBudget);
  }

  // In a normal rounded rectangle (cornerSmoothing = 0), this is 90
  // The larger the smoothing, the smaller the arc
  const arcMeasure = 90 * (1 - cornerSmoothing);
  const arcSectionLength =
    Math.sin(toRadians(arcMeasure / 2)) * cornerRadius * Math.sqrt(2);

  // In the article this is the distance between 2 control points: P3 and P4
  const angleAlpha = (90 - arcMeasure) / 2;
  const p3ToP4Distance = cornerRadius * Math.tan(toRadians(angleAlpha / 2));

  // a, b, c and d are from figure 11.1 in the article
  const angleBeta = 45 * cornerSmoothing;
  const c = p3ToP4Distance * Math.cos(toRadians(angleBeta));
  const d = c * Math.tan(toRadians(angleBeta));

  let b = (p - arcSectionLength - c - d) / 3;
  let a = 2 * b;

  // Adjust the P1 and P2 control points if there's not enough space left
  if (preserveSmoothing && p > roundingAndSmoothingBudget) {
    const p1ToP3MaxDistance =
      roundingAndSmoothingBudget - d - arcSectionLength - c;

    // Try to maintain some distance between P1 and P2 so the curve wouldn't look weird
    const minA = p1ToP3MaxDistance / 6;
    const maxB = p1ToP3MaxDistance - minA;

    b = Math.min(b, maxB);
    a = p1ToP3MaxDistance - b;
    p = Math.min(p, roundingAndSmoothingBudget);
  }

  return {
    a,
    b,
    c,
    d,
    p,
    arcSectionLength,
    cornerRadius,
  };
}
